import React, { useEffect, useRef, useState } from "react";

import api from "../services/api";
import { isAndroidPlayConsumptionOnly } from "../platform";

// Akili, l'assistant Quick Learning (« intelligence, esprit vif » en swahili) : répond aux questions d'utilisation à partir d'une
// aide préparée par l'équipe (aucune IA en ligne, aucun coût). Le serveur ne
// renvoie que les réponses de l'espace de la personne connectée.
const NOM = "Akili";
const MESSAGE = "Akili répond aux questions sur l'utilisation de votre espace, à partir de l'aide préparée par l'équipe (sans IA en ligne).";

const STORAGE_KEY = "ql_ai_fab_pos";
const SIZE = 56;
const MARGIN = 12;
const DRAG_THRESHOLD = 5;

function clamp(x, y) {
  // Applications téléphone : la bulle reste au-dessus de la barre d'accès rapide.
  const barre = document.body.classList.contains("ql-avec-barre-rapide") ? 72 : 0;
  const maxX = Math.max(MARGIN, window.innerWidth - SIZE - MARGIN);
  const maxY = Math.max(MARGIN, window.innerHeight - SIZE - MARGIN - barre);
  return { x: Math.min(Math.max(x, MARGIN), maxX), y: Math.min(Math.max(y, MARGIN), maxY) };
}

function defaultPosition() {
  return clamp(window.innerWidth - SIZE - MARGIN, window.innerHeight - SIZE - MARGIN);
}

function loadPosition() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return defaultPosition();
    const parsed = JSON.parse(raw);
    if (!Number.isFinite(parsed?.x) || !Number.isFinite(parsed?.y)) return defaultPosition();
    return clamp(parsed.x, parsed.y);
  } catch {
    return defaultPosition();
  }
}

export default function AIAssistantComingSoon() {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(loadPosition);
  const [dragging, setDragging] = useState(false);
  const [messages, setMessages] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [espace, setEspace] = useState("");
  const [question, setQuestion] = useState("");
  const [busy, setBusy] = useState(false);
  const closeButtonRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const dragRef = useRef(null);

  const ask = async (texte) => {
    setBusy(true);
    try {
      // Applis Android Play et iPhone : l'assistant ne parle jamais de prix ni de paiement.
      const params = { ...(texte == null ? {} : { q: texte }), ...(isAndroidPlayConsumptionOnly ? { consultation: 1 } : {}) };
      const { data } = await api.get("assistant/", { params });
      setMessages((list) => [...list, { from: "assistant", text: data.reponse, kind: data.type }]);
      setSuggestions(data.suggestions || []);
      if (data.espace !== undefined) setEspace(data.espace);
    } catch {
      setMessages((list) => [...list, { from: "assistant", text: "Je ne peux pas répondre pour le moment. Vérifiez votre connexion puis réessayez.", kind: "erreur" }]);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (!open) return undefined;
    if (!messages.length) ask(null);
    inputRef.current?.focus();
    const closeOnEscape = event => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", closeOnEscape);
    return () => document.removeEventListener("keydown", closeOnEscape);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps -- accueil chargé à la première ouverture

  useEffect(() => {
    listRef.current?.scrollTo?.({ top: listRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, busy]);

  const send = (texte) => {
    const propre = String(texte || "").trim();
    if (!propre || busy) return;
    setMessages((list) => [...list, { from: "user", text: propre }]);
    setQuestion("");
    ask(propre);
  };

  useEffect(() => {
    const onResize = () => setPos(current => clamp(current.x, current.y));
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Le drag est détecté via Pointer Events (souris + tactile), mais l'ouverture de la
  // modale passe par onClick : c'est le seul chemin qui fonctionne aussi au clavier
  // (Entrée/Espace sur le bouton focus). suppressClickRef évite que le "click" de
  // synthèse qui suit un pointerup rouvre la modale juste après un drag.
  const suppressClickRef = useRef(false);

  const onPointerDown = event => {
    if (event.button !== undefined && event.button !== 0) return;
    dragRef.current = {
      startX: event.clientX, startY: event.clientY,
      originX: pos.x, originY: pos.y, moved: false,
    };
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };

  const onPointerMove = event => {
    const drag = dragRef.current;
    if (!drag) return;
    const dx = event.clientX - drag.startX;
    const dy = event.clientY - drag.startY;
    if (!drag.moved && Math.hypot(dx, dy) < DRAG_THRESHOLD) return;
    drag.moved = true;
    if (!dragging) setDragging(true);
    setPos(clamp(drag.originX + dx, drag.originY + dy));
  };

  const onPointerUp = event => {
    const drag = dragRef.current;
    dragRef.current = null;
    event.currentTarget.releasePointerCapture?.(event.pointerId);
    if (drag?.moved) {
      setDragging(false);
      suppressClickRef.current = true;
      setPos(current => {
        const next = clamp(current.x, current.y);
        try { localStorage.setItem(STORAGE_KEY, JSON.stringify(next)); } catch { /* stockage indisponible */ }
        return next;
      });
    }
  };

  const onClick = () => {
    if (suppressClickRef.current) {
      suppressClickRef.current = false;
      return;
    }
    setOpen((current) => !current);
  };

  return <>
    <button
      type="button"
      className={`ql-ai-entry${dragging ? " is-dragging" : ""}`}
      style={{ left: pos.x, top: pos.y, right: "auto", bottom: "auto" }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      onClick={onClick}
      aria-haspopup="dialog"
      aria-expanded={open}
      aria-label="Ouvrir Akili, l'assistant Quick Learning"
      title="Akili, l'assistant Quick Learning"
    >
      <i className="bi bi-stars" aria-hidden="true"></i>
      <span className="ql-sr-only">{NOM}</span>
    </button>
    {open && (
      <section className="ql-assistant" role="dialog" aria-modal="false" aria-labelledby="ql-assistant-titre">
        <header className="ql-assistant-tete">
          <span className="ql-assistant-avatar" aria-hidden="true"><i className="bi bi-stars"></i></span>
          <div>
            <h2 id="ql-assistant-titre" translate="no">{NOM}</h2>
            <small>Assistant Quick Learning{espace ? ` · Espace ${espace}` : ""}</small>
          </div>
          <button ref={closeButtonRef} type="button" className="ql-assistant-fermer" onClick={() => setOpen(false)} aria-label="Fermer Akili">
            <i className="bi bi-x-lg" aria-hidden="true"></i>
          </button>
        </header>
        <div className="ql-assistant-fil" ref={listRef} aria-live="polite">
          {messages.map((m, i) => (
            <p key={i} className={`ql-assistant-bulle ql-assistant-bulle--${m.from}${m.kind === "hors_espace" ? " ql-assistant-bulle--douce" : ""}${m.kind === "rappels" ? " ql-assistant-bulle--rappels" : ""}`}>{m.text}</p>
          ))}
          {busy && <p className="ql-assistant-bulle ql-assistant-bulle--assistant ql-assistant-ecrit" aria-label="Akili écrit"><span></span><span></span><span></span></p>}
        </div>
        {suggestions.length > 0 && (
          <div className="ql-assistant-suggestions" aria-label="Questions suggérées">
            {suggestions.map((s) => <button type="button" key={s} onClick={() => send(s)} disabled={busy}>{s}</button>)}
          </div>
        )}
        <form className="ql-assistant-saisie" onSubmit={(e) => { e.preventDefault(); send(question); }}>
          <input ref={inputRef} value={question} onChange={(e) => setQuestion(e.target.value)} maxLength={300}
            placeholder="Votre question…" aria-label="Votre question" />
          <button type="submit" disabled={busy || !question.trim()} aria-label="Envoyer"><i className="bi bi-send-fill" aria-hidden="true"></i></button>
        </form>
        <p className="ql-assistant-note">{MESSAGE}</p>
      </section>
    )}
  </>;
}

export { MESSAGE };
